import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inboxService } from '@/services/inbox.service';

export function useInboxConversations(params: {
  filter?: string;
  channel_type?: string;
  limit?: number;
  page?: number;
}) {
  return useQuery({
    queryKey: ['inbox-conversations', params],
    queryFn: () => inboxService.getConversations(params),
    refetchInterval: 10000,
  });
}

export function useChannelAvailability(customerId: string | undefined) {
  return useQuery({
    queryKey: ['channel-availability', customerId],
    queryFn: () => inboxService.getChannelAvailability(customerId!),
    enabled: !!customerId,
    refetchInterval: 30000,
  });
}

export function useSendInboxMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content, channelId }: { conversationId: string; content: string; channelId?: string }) =>
      inboxService.sendHumanMessage(conversationId, content, channelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation-messages'] });
    },
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: string; content: string }) =>
      inboxService.createNote(conversationId, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation-messages'] });
    },
  });
}

export function useAssignConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, userId }: { conversationId: string; userId: string | null }) =>
      inboxService.assignConversation(conversationId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-conversations'] });
    },
  });
}

export function useUpdateConversationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, status }: { conversationId: string; status: string }) =>
      inboxService.updateConversationStatus(conversationId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-conversations'] });
    },
  });
}

export function useUpdateCustomerProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: { name?: string; email?: string; phone?: string } }) =>
      inboxService.updateCustomerProfile(customerId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-conversations'] });
      qc.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}

export function useUpdateCustomerTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, tags }: { customerId: string; tags: { is_high_potential?: boolean; is_blocked?: boolean; custom_tags?: string[] } }) =>
      inboxService.updateCustomerTags(customerId, tags),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-conversations'] });
      qc.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}
